import { useMemo } from 'react'
import { PivotResult } from 'schemas/PivotResultSchema'
import ComparisonMode, { ComparisonModeStrings } from 'enums/ComparisonMode'
import CrosstabDisplayTypeDetail from 'enums/CrosstabDisplayTypeDetail'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { useDashboardWidgetData } from 'hooks/useWidgetState'
import { DashboardWidget } from 'features/widget/types'
import { CrosstabWidgetsType, PivotWithCellMarker } from '../../types'
import {
  getRowData,
  getColumnData,
  getWidgetSettingConfig,
  getComparisonMode
} from '../utils/crosstabDataUtils'
import { ROW_GRAND_TOTAL_ID } from '../constants'

const getCrosstabData = (
  widgetData: DashboardWidget<PivotResult>,
  displayRowGrandTotal: boolean,
  comparisonMode: ComparisonModeStrings
) => {
  const baseResult = widgetData.data[0]
  const rowGrandTotal = widgetData.data[1]

  let crossTabsBaseResult: PivotWithCellMarker[] = []
  let crossTabsRowGrandTotal: PivotWithCellMarker[] = []

  crossTabsBaseResult = baseResult
    .results!.map((serviceResult) =>
      serviceResult.data.crossTabs!.map((pivot) => ({
        ...pivot,
        cellMarker: CrosstabDisplayTypeDetail.NONE
      }))
    )
    .flat()

  if (displayRowGrandTotal) {
    crossTabsRowGrandTotal = rowGrandTotal
      .results!.map((serviceResult) =>
        serviceResult.data.crossTabs!.map((pivot) => {
          const pivotWithCellMarker = {
            ...pivot,
            cellMarker: CrosstabDisplayTypeDetail.ROW_GRAND_TOTAL
          }
          switch (comparisonMode) {
            case ComparisonMode.THEMES:
              return {
                ...pivotWithCellMarker,
                themeId: Number(ROW_GRAND_TOTAL_ID)
              }
            case ComparisonMode.TAGS:
              return {
                ...pivotWithCellMarker,
                tagName: ROW_GRAND_TOTAL_ID
              }
            case ComparisonMode.DOC_HIERARCHIES:
              return {
                ...pivotWithCellMarker,
                docName: ROW_GRAND_TOTAL_ID
              }
            case ComparisonMode.NONE:
            default:
              return pivotWithCellMarker
          }
        })
      )
      .flat()
  }

  return crossTabsBaseResult.concat(crossTabsRowGrandTotal)
}

const useCrosstabData = (widgetId: number, isAudienceQuery: boolean) => {
  const widgetSetting = useDashboardWidgetSetting<CrosstabWidgetsType>(widgetId)
  const widgetData = useDashboardWidgetData<PivotResult>(widgetId)
  const { displayRowGrandTotal } = getWidgetSettingConfig(widgetSetting)
  const crossTabs = useMemo(
    () => getCrosstabData(widgetData, displayRowGrandTotal, widgetSetting.colComparisonMode!),
    [widgetData, displayRowGrandTotal, widgetSetting.colComparisonMode]
  )
  const { rowIds, rowNames } = useMemo(() => getRowData(widgetSetting), [widgetSetting])
  const { columnIds, columnNames } = useMemo(() => getColumnData(widgetSetting, isAudienceQuery), [
    widgetSetting,
    isAudienceQuery
  ])

  const { colComparisonMode, rowComparisonMode } = getComparisonMode(widgetSetting)

  return {
    crossTabs,
    rowIds,
    rowNames,
    columnIds,
    columnNames,
    rowComparisonMode,
    colComparisonMode
  }
}

export default useCrosstabData
